import React, { useState, useEffect } from "react";
import api from "./api";
import { useAuth } from "./AuthContext";
import {
  FaUpload,
  FaFolderOpen,
  FaFileAlt,
  FaTimes,
  FaCheckCircle,
  FaTimesCircle,
  FaInfoCircle,
  FaRedo,
  FaSearch,
} from "react-icons/fa";

function UploadPage() {
  const { isAdmin } = useAuth();
  const [files, setFiles] = useState([]);
  const [fileProgress, setFileProgress] = useState({});
  const [loading, setLoading] = useState(false);
  const [isDemo, setIsDemo] = useState(false);
  const [uploadType, setUploadType] = useState("files");
  const [uploadDone, setUploadDone] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [dragging, setDragging] = useState(false);
  const [reviews, setReviews] = useState(null);
  const [reviewing, setReviewing] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      api.get("/api/listCompaniesFun")
        .then((res) => {
          setCompanies(res.data);
          if (res.data.length > 0) setSelectedCompany(res.data[0]);
        })
        .catch(() => {});
    }
  }, [isAdmin]);

  const handleFilesChange = (event) => {
    const items = event.target.files;
    const filesArray = Array.from(items).filter(
      (f) => f.name.toLowerCase().endsWith(".xml")
    );
    if (filesArray.length === 0) return;
    setFiles((prev) => [...prev, ...filesArray]);
    const newProgress = {};
    filesArray.forEach((file) => {
      newProgress[file.name] = { progress: 0, status: "ready", message: "Spreman za slanje" };
    });
    setFileProgress((prev) => ({ ...prev, ...newProgress }));
    setUploadDone(false);
  };

  const handleRemoveFile = (index) => {
    const fileToRemove = files[index];
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setFileProgress((prev) => {
      const updated = { ...prev };
      delete updated[fileToRemove.name];
      return updated;
    });
  };

  const handleClearAll = () => {
    setFiles([]);
    setFileProgress({});
    setUploadDone(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      (f) => f.name.toLowerCase().endsWith(".xml")
    );
    if (droppedFiles.length === 0) return;
    setFiles((prev) => [...prev, ...droppedFiles]);
    const newProgress = {};
    droppedFiles.forEach((file) => {
      newProgress[file.name] = { progress: 0, status: "ready", message: "Spreman za slanje" };
    });
    setFileProgress((prev) => ({ ...prev, ...newProgress }));
    setUploadDone(false);
  };

  const extractError = (error) => {
    if (error.response?.data) {
      if (Array.isArray(error.response.data) && error.response.data[0]?.message) {
        return error.response.data[0].message;
      }
      if (error.response.data.message) return error.response.data.message;
      if (typeof error.response.data === "string") return error.response.data;
    }
    return error.message || "Nepoznata greska";
  };

  // Reads the invoices without sending them, so the bookkeeper can see what is
  // in each one and what is wrong with it while it is still fixable. Nothing
  // reaches eFaktura, so this is safe to run repeatedly.
  const handleReview = async () => {
    setReviewing(true);
    setReviews(null);
    try {
      const contents = await Promise.all(
        files.map(async (f) => ({ name: f.name, content: await f.text() }))
      );
      const url = isDemo && isAdmin ? "/api/uploadFunDemo" : "/api/uploadFun";
      const res = await api.post(`${url}?preview=1`, { files: contents });
      setReviews(res.data);
    } catch (error) {
      setReviews([
        { name: "", greske: [extractError(error)], mozeDaSePosalje: false },
      ]);
    } finally {
      setReviewing(false);
    }
  };

  const money = (n) =>
    (Number(n) || 0).toLocaleString("sr-RS", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const blockedCount = (reviews || []).filter((r) => !r.mozeDaSePosalje).length;

  // Sends a single file. adjustIssueDate=true re-dates the invoice to today —
  // only used when the client explicitly clicks the resend button after eFaktura
  // rejected it for a wrong issue date.
  const sendFile = async (file, adjustIssueDate = false) => {
    const uploadUrl =
      isDemo && isAdmin ? `/api/uploadFunDemo` : `/api/uploadFun`;

    setFileProgress((prev) => ({
      ...prev,
      [file.name]: {
        ...prev[file.name],
        status: "sending",
        message: adjustIssueDate
          ? "Ponovno slanje sa danasnjim datumom..."
          : "Slanje u toku...",
        progress: 0,
        canAdjust: false,
      },
    }));

    try {
      const fileContent = await file.text();
      const response = await api.post(
        uploadUrl,
        { files: [{ name: file.name, content: fileContent, adjustIssueDate }] },
        {
          headers: { "Content-Type": "application/json" },
          onUploadProgress: (progressEvent) => {
            const progress = progressEvent.total
              ? Math.round((progressEvent.loaded / progressEvent.total) * 100)
              : 0;
            setFileProgress((prev) => ({
              ...prev,
              [file.name]: { ...prev[file.name], progress },
            }));
          },
        }
      );

      const results = Array.isArray(response.data)
        ? response.data
        : typeof response.data === "string"
        ? JSON.parse(response.data)
        : [];

      const result = results[0] || {};
      const isSuccess = result.success !== false;
      let message =
        result.message ||
        (isSuccess ? "Faktura uspesno poslata!" : "Slanje neuspesno");
      if (isSuccess && result.issueDateAdjusted) {
        message += ` (datum izdavanja: ${result.issueDateAdjusted.from} → ${result.issueDateAdjusted.to})`;
      }

      setFileProgress((prev) => ({
        ...prev,
        [file.name]: {
          ...prev[file.name],
          status: isSuccess ? "success" : "error",
          message,
          progress: 100,
          canAdjust: result.canAdjustIssueDate === true,
        },
      }));
    } catch (error) {
      setFileProgress((prev) => ({
        ...prev,
        [file.name]: {
          ...prev[file.name],
          status: "error",
          message: extractError(error),
          progress: 100,
          canAdjust: false,
        },
      }));
    }
  };

  const handleUpload = async () => {
    setLoading(true);
    setUploadDone(false);
    for (const file of files) {
      await sendFile(file, false);
    }
    setLoading(false);
    setUploadDone(true);
  };

  const handleResendWithToday = async (file) => {
    setLoading(true);
    await sendFile(file, true);
    setLoading(false);
  };

  const getStatusIcon = (status) => {
    if (status === "success")
      return <FaCheckCircle className="status-icon-success" />;
    if (status === "error")
      return <FaTimesCircle className="status-icon-error" />;
    return null;
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "ready": return "Spreman";
      case "sending": return "Slanje...";
      case "success": return "Poslato";
      case "error": return "Greska";
      default: return "";
    }
  };

  const successCount = Object.values(fileProgress).filter((f) => f.status === "success").length;
  const errorCount = Object.values(fileProgress).filter((f) => f.status === "error").length;

  return (
    <div className="page-card">
      <h2 className="page-title">Slanje faktura</h2>

      {/* Info box for company users (noobs) */}
      {!isAdmin && (
        <div className="info-box">
          <FaInfoCircle className="info-icon" />
          <div>
            <strong>Kako poslati fakture:</strong>
            <ol className="info-steps">
              <li>Izaberite XML fajlove klikom na dugme ispod</li>
              <li>Proverite listu fajlova</li>
              <li>Kliknite "Posalji" i sacekajte rezultat</li>
            </ol>
          </div>
        </div>
      )}

      {/* Admin: company context */}
      {isAdmin && companies.length > 0 && (
        <div className="form-group">
          <label>Slanje u ime firme</label>
          <select
            className="form-control"
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
          >
            {companies.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <small style={{ color: "var(--text-muted)", marginTop: 4, display: "block" }}>
            Firma se odredjuje automatski iz XML fajla
          </small>
        </div>
      )}

      {/* File/Folder toggle */}
      <div className="upload-type-group">
        <button
          className={`upload-type-btn ${uploadType === "files" ? "active" : ""}`}
          onClick={() => setUploadType("files")}
        >
          <FaFileAlt style={{ marginRight: 6 }} /> Fajlovi
        </button>
        <button
          className={`upload-type-btn ${uploadType === "folder" ? "active" : ""}`}
          onClick={() => setUploadType("folder")}
        >
          <FaFolderOpen style={{ marginRight: 6 }} /> Folder
        </button>
      </div>

      {/* File picker */}
      <label
        className={`file-input-label${dragging ? " file-input-dragging" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {uploadType === "files" ? (
          <>
            <FaFileAlt style={{ marginRight: 8 }} />
            Kliknite ovde da izaberete XML fajlove
            <input
              type="file"
              multiple
              accept=".xml"
              onChange={handleFilesChange}
              style={{ display: "none" }}
            />
          </>
        ) : (
          <>
            <FaFolderOpen style={{ marginRight: 8 }} />
            Kliknite ovde da izaberete folder sa XML fajlovima
            <input
              type="file"
              directory=""
              webkitdirectory=""
              onChange={handleFilesChange}
              style={{ display: "none" }}
            />
          </>
        )}
      </label>

      {/* File list */}
      {files.length > 0 && (
        <>
          <div className="file-list-header">
            <span className="file-list-count">
              {files.length} {files.length === 1 ? "fajl" : "fajlova"} izabrano
            </span>
            <button className="btn-text" onClick={handleClearAll}>
              <FaTimes style={{ marginRight: 4 }} /> Obrisi sve
            </button>
          </div>

          <div className="file-list">
            {files.map((file, index) => (
              <div
                key={index}
                className={`file-item ${
                  fileProgress[file.name]?.status === "success"
                    ? "file-item-success"
                    : fileProgress[file.name]?.status === "error"
                    ? "file-item-error"
                    : ""
                }`}
              >
                <div className="file-item-header">
                  <span className="file-name">
                    {getStatusIcon(fileProgress[file.name]?.status)}
                    {file.name}
                  </span>
                  <div className="file-item-right">
                    <span className={`file-status file-status-${fileProgress[file.name]?.status || "ready"}`}>
                      {getStatusLabel(fileProgress[file.name]?.status)}
                    </span>
                    {!loading && fileProgress[file.name]?.status !== "success" && (
                      <button
                        className="btn-remove"
                        onClick={() => handleRemoveFile(index)}
                        title="Ukloni"
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                </div>
                {fileProgress[file.name]?.status === "sending" && (
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar-fill"
                      style={{ width: `${fileProgress[file.name]?.progress}%` }}
                    />
                  </div>
                )}
                {fileProgress[file.name]?.message &&
                  fileProgress[file.name]?.status !== "ready" &&
                  fileProgress[file.name]?.status !== "sending" && (
                  <div className={`file-result-message ${
                    fileProgress[file.name]?.status === "success"
                      ? "file-result-success"
                      : "file-result-error"
                  }`}>
                    {fileProgress[file.name]?.message}
                  </div>
                )}
                {fileProgress[file.name]?.canAdjust && (
                  <div className="file-result-action" style={{ marginTop: 8 }}>
                    <button
                      className="btn-secondary"
                      onClick={() => handleResendWithToday(file)}
                      disabled={loading}
                    >
                      <FaRedo style={{ marginRight: 6 }} />
                      Posalji ponovo sa danasnjim datumom
                    </button>
                    <small
                      style={{
                        display: "block",
                        marginTop: 4,
                        color: "var(--text-muted)",
                      }}
                    >
                      eFaktura zahteva da datum izdavanja bude danasnji. Ovim se
                      menja datum izdavanja fakture na danasnji datum.
                    </small>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Upload summary */}
      {uploadDone && (
        <div className={`upload-summary ${
          errorCount === 0 ? "upload-summary-success" : "upload-summary-error"
        }`}>
          {errorCount === 0 ? (
            <><FaCheckCircle style={{ marginRight: 8 }} /> Sve fakture ({successCount}) su uspesno poslate!</>
          ) : (
            <>
              <FaTimesCircle style={{ marginRight: 8 }} />
              Poslato: {successCount} od {successCount + errorCount}.
              {errorCount > 0 && ` Greske: ${errorCount}. Pogledajte detalje iznad.`}
            </>
          )}
        </div>
      )}

      {/* Admin controls */}
      {isAdmin && (
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={isDemo}
            onChange={() => setIsDemo(!isDemo)}
          />
          Demo rezim (testno slanje)
        </label>
      )}

      {/* Review before sending */}
      {reviews && (
        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <div
            className={`upload-summary ${
              blockedCount === 0 ? "upload-summary-success" : "upload-summary-error"
            }`}
          >
            {blockedCount === 0
              ? `Pregledano: ${reviews.length}. Sve fakture mogu da se posalju.`
              : `Pregledano: ${reviews.length}. Ne moze da se posalje: ${blockedCount}.`}
          </div>

          {reviews.map((r, i) => (
            <div
              key={`${r.name}-${i}`}
              className="file-item"
              style={{
                borderLeft: `4px solid ${
                  r.mozeDaSePosalje ? "var(--success-color, #2e7d32)" : "var(--error-color, #c62828)"
                }`,
                marginTop: 10,
                padding: 12,
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                {r.name} {r.brojFakture ? `- faktura ${r.brojFakture}` : ""}
              </div>

              {r.prodavac && (
                <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
                  Prodavac: {r.prodavac.naziv || "(nema naziv)"} ({r.prodavac.pib})
                  {"  |  "}Kupac: {r.kupac?.naziv || "(nema naziv)"} ({r.kupac?.pib})
                  {"  |  "}Datum prometa: {r.datumPrometa || "-"}
                </div>
              )}

              {r.stavke && r.stavke.length > 0 && (
                <div className="users-table-wrapper" style={{ marginTop: 10 }}>
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>Rb.</th>
                        <th>Naziv</th>
                        <th style={{ textAlign: "right" }}>Kolicina</th>
                        <th style={{ textAlign: "right" }}>Cena</th>
                        <th style={{ textAlign: "right" }}>Iznos</th>
                        <th style={{ textAlign: "right" }}>PDV %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.stavke.map((st, j) => (
                        <tr key={j}>
                          <td>{st.redniBroj}</td>
                          <td>{st.naziv}</td>
                          <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                            {st.kolicina}
                          </td>
                          <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                            {money(st.cena)}
                          </td>
                          <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                            {money(st.iznos)}
                          </td>
                          <td style={{ textAlign: "right" }}>{st.stopaPdv}</td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: "2px solid var(--border-color, #ccc)" }}>
                        <td colSpan={4}>
                          <strong>Osnovica / PDV / Za placanje</strong>
                        </td>
                        <td colSpan={2} style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                          <strong>
                            {money(r.osnovica)} / {money(r.pdv)} / {money(r.zaPlacanje)}
                          </strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {(r.greske || []).map((g, j) => (
                <div key={`g${j}`} className="status-message error" style={{ marginTop: 8 }}>
                  {g}
                </div>
              ))}
              {(r.upozorenja || []).map((u, j) => (
                <div
                  key={`u${j}`}
                  style={{
                    marginTop: 6,
                    fontSize: 13,
                    color: "var(--warning-color, #b26a00)",
                  }}
                >
                  Upozorenje: {u}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          className="btn-secondary"
          onClick={handleReview}
          disabled={reviewing || loading || files.length === 0}
        >
          <FaSearch style={{ marginRight: 6 }} />
          {reviewing ? "Pregled..." : "Pregledaj"}
        </button>
        <button
          className="btn-primary"
          onClick={handleUpload}
          disabled={loading || files.length === 0}
          style={{ flex: 1 }}
        >
          {loading ? (
            "Slanje u toku..."
          ) : (
            <>
              <FaUpload style={{ marginRight: 8 }} />
              Posalji {files.length > 0 ? `(${files.length})` : ""}
            </>
          )}
        </button>
        {uploadDone && (
          <button className="btn-secondary" onClick={handleClearAll}>
            <FaRedo style={{ marginRight: 6 }} /> Ponovo
          </button>
        )}
      </div>
    </div>
  );
}

export default UploadPage;
